<template>
  <div class="questionnaire-result-container">
    <!-- 页面标题 -->
    <el-card class="page-header-card">
      <div class="page-header">
        <div class="header-left">
          <h2>问卷结果管理</h2>
          <el-tag type="primary" v-if="userRole === 'doctor'">医生工作台</el-tag>
          <el-tag type="success" v-else-if="userRole === 'manager'">个案管理师工作台</el-tag>
        </div>
        <div class="header-right">
          <el-button type="primary" icon="el-icon-download" @click="exportResults">导出结果</el-button>
          <el-button icon="el-icon-refresh" @click="refreshData">刷新数据</el-button>
        </div>
      </div>
    </el-card>

    <!-- 搜索筛选区 -->
    <el-card class="search-card">
      <el-form :inline="true" :model="searchForm" ref="searchForm" size="small">
        <el-form-item label="问卷类型">
          <el-select v-model="searchForm.questionnaireType" placeholder="全部类型" clearable>
            <el-option label="满意度问卷" value="satisfaction"></el-option>
            <el-option label="随访问卷" value="followup"></el-option>
            <el-option label="评估问卷" value="assessment"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="患者姓名">
          <el-input v-model="searchForm.patientName" placeholder="请输入患者姓名" clearable></el-input>
        </el-form-item>
        <el-form-item label="提交时间">
          <el-date-picker
            v-model="searchForm.submitTime"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            clearable>
          </el-date-picker>
        </el-form-item>
        <el-form-item label="完成状态">
          <el-select v-model="searchForm.status" placeholder="全部状态" clearable>
            <el-option label="已完成" value="completed"></el-option>
            <el-option label="未完成" value="incomplete"></el-option>
            <el-option label="已过期" value="expired"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="异常标记">
          <el-select v-model="searchForm.abnormal" placeholder="全部" clearable>
            <el-option label="有异常标记" value="true"></el-option>
            <el-option label="无异常标记" value="false"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 患者问卷列表 -->
    <el-card class="result-list-card">
      <div slot="header" class="card-header">
        <span>患者问卷结果列表</span>
        <span class="result-count">共 {{total}} 条结果</span>
      </div>
      
      <el-table
        :data="resultList"
        border
        v-loading="loading"
        highlight-current-row
        @row-click="handleRowClick"
        :row-class-name="getRowClass"
      >
        <el-table-column type="index" label="序号" width="60"></el-table-column>
        <el-table-column prop="patientName" label="患者姓名" width="100"></el-table-column>
        <el-table-column prop="patientId" label="病历号" width="120"></el-table-column>
        <el-table-column prop="questionnaireName" label="问卷名称" min-width="180"></el-table-column>
        <el-table-column prop="questionnaireType" label="问卷类型" width="120">
          <template slot-scope="scope">
            <el-tag :type="getQuestionnaireTypeTag(scope.row.questionnaireType)">
              {{ getQuestionnaireTypeText(scope.row.questionnaireType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="submitTime" label="提交时间" width="180"></el-table-column>
        <el-table-column prop="status" label="完成状态" width="100">
          <template slot-scope="scope">
            <el-tag :type="getStatusTag(scope.row.status)">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="异常标记" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.abnormalMark" type="danger">异常</el-tag>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" @click.stop="viewDetail(scope.row)">查看详情</el-button>
            <el-button 
              size="mini" 
              :type="scope.row.abnormalMark ? 'danger' : 'warning'" 
              @click.stop="markAbnormal(scope.row)"
            >
              {{ scope.row.abnormalMark ? '取消标记' : '标记异常' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </el-card>

    <!-- 问卷详情对话框 -->
    <el-dialog
      title="问卷详情"
      :visible.sync="detailDialogVisible"
      width="80%"
      :before-close="handleDetailDialogClose"
    >
      <div v-if="currentDetail" class="questionnaire-detail">
        <div class="detail-header">
          <h3>{{ currentDetail.questionnaireName }}</h3>
          <div class="detail-meta">
            <span><strong>患者姓名：</strong>{{ currentDetail.patientName }}</span>
            <span><strong>病历号：</strong>{{ currentDetail.patientId }}</span>
            <span><strong>提交时间：</strong>{{ currentDetail.submitTime }}</span>
            <span>
              <strong>完成状态：</strong>
              <el-tag :type="getStatusTag(currentDetail.status)" size="small">{{ getStatusText(currentDetail.status) }}</el-tag>
            </span>
          </div>
        </div>

        <el-divider content-position="center">问卷内容</el-divider>
        
        <!-- 问卷答案列表 -->
        <div class="answer-list">
          <div v-for="(question, index) in currentDetail.questions" :key="index" class="question-item">
            <div class="question-header">
              <span class="question-number">Q{{ index + 1 }}</span>
              <div class="question-title">{{ question.title }}</div>
              <div class="question-flags">
                <el-tag size="small" type="danger" v-if="question.isAbnormal">异常回答</el-tag>
                <el-tag size="small" type="info" v-if="question.required">必答题</el-tag>
              </div>
            </div>
            
            <!-- 不同类型问题的答案显示 -->
            <div class="question-answer">
              <!-- 单选题答案 -->
              <template v-if="question.type === 'radio'">
                <div class="answer-content">
                  <span class="answer-label">患者回答：</span>
                  <span>{{ question.answer }}</span>
                </div>
              </template>
              
              <!-- 多选题答案 -->
              <template v-else-if="question.type === 'checkbox'">
                <div class="answer-content">
                  <span class="answer-label">患者回答：</span>
                  <el-tag 
                    v-for="(item, i) in question.answer" 
                    :key="i" 
                    size="small" 
                    style="margin-right: 5px"
                  >{{ item }}</el-tag>
                </div>
              </template>
              
              <!-- 文本题答案 -->
              <template v-else-if="question.type === 'text'">
                <div class="answer-content">
                  <span class="answer-label">患者回答：</span>
                  <div class="text-answer">{{ question.answer }}</div>
                </div>
              </template>
              
              <!-- 评分题答案 -->
              <template v-else-if="question.type === 'rate'">
                <div class="answer-content">
                  <span class="answer-label">患者回答：</span>
                  <el-rate 
                    v-model="question.answer" 
                    disabled 
                    show-score 
                    text-color="#ff9900"
                  ></el-rate>
                </div>
              </template>
              
              <!-- 数字题答案 -->
              <template v-else-if="question.type === 'number'">
                <div class="answer-content">
                  <span class="answer-label">患者回答：</span>
                  <span class="number-answer">{{ question.answer }} {{ question.unit || '' }}</span>
                </div>
              </template>
            </div>
            
            <!-- 医生备注区域 -->
            <div class="question-remarks">
              <div class="remark-header">
                <span>医疗人员备注：</span>
                <el-checkbox 
                  v-model="question.isAbnormal" 
                  @change="markQuestionAbnormal(index)"
                >标记为异常答案</el-checkbox>
              </div>
              <el-input
                type="textarea"
                :rows="2"
                placeholder="在此添加关于该问题回答的备注..."
                v-model="question.remark"
                @blur="saveQuestionRemark(index)"
              ></el-input>
            </div>
          </div>
        </div>
        
        <el-divider content-position="center">总体评估</el-divider>
        
        <!-- 总体评估区域 -->
        <div class="overall-assessment">
          <el-form :model="currentDetail.assessment" label-width="120px">
            <el-form-item label="问卷得分">
              <el-input-number 
                v-model="currentDetail.assessment.score" 
                :min="0" 
                :max="100" 
                :step="5"
                :precision="0"
              ></el-input-number>
              <span class="score-hint">（满分100分）</span>
            </el-form-item>
            
            <el-form-item label="健康状态评级">
              <el-select v-model="currentDetail.assessment.healthStatus">
                <el-option label="正常" value="normal"></el-option>
                <el-option label="需关注" value="attention"></el-option>
                <el-option label="异常" value="abnormal"></el-option>
                <el-option label="危急" value="critical"></el-option>
              </el-select>
            </el-form-item>
            
            <el-form-item label="随访建议">
              <el-select v-model="currentDetail.assessment.followUpSuggestion">
                <el-option label="继续当前治疗方案" value="continue"></el-option>
                <el-option label="调整治疗方案" value="adjust"></el-option>
                <el-option label="安排面诊" value="visit"></el-option>
                <el-option label="加强随访频率" value="increase"></el-option>
              </el-select>
            </el-form-item>
            
            <el-form-item label="综合备注">
              <el-input
                type="textarea"
                :rows="4"
                placeholder="请输入对该患者问卷的综合评价与建议..."
                v-model="currentDetail.assessment.remarks"
              ></el-input>
            </el-form-item>
          </el-form>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="saveDetailAndClose">保存评估</el-button>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
        <el-button type="success" icon="el-icon-message" @click="contactPatient">联系患者</el-button>
      </span>
    </el-dialog>

    <!-- 标记异常对话框 -->
    <el-dialog
      title="异常标记"
      :visible.sync="markDialogVisible"
      width="500px"
    >
      <el-form :model="markForm" label-width="120px" ref="markForm">
        <el-form-item label="异常类型" prop="type">
          <el-select v-model="markForm.type" placeholder="请选择异常类型">
            <el-option label="健康状况异常" value="healthIssue"></el-option>
            <el-option label="回答不一致" value="inconsistency"></el-option>
            <el-option label="需要复查" value="recheck"></el-option>
            <el-option label="需要面诊" value="visitNeeded"></el-option>
            <el-option label="其他" value="other"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="异常级别" prop="level">
          <el-rate 
            v-model="markForm.level" 
            :colors="['#99A9BF', '#F7BA2A', '#FF4949']"
            show-text
            :texts="['一般', '重要', '紧急', '很紧急', '极其紧急']">
          </el-rate>
        </el-form-item>
        
        <el-form-item label="备注说明" prop="remark">
          <el-input
            type="textarea"
            :rows="4"
            placeholder="请输入异常情况说明..."
            v-model="markForm.remark">
          </el-input>
        </el-form-item>
        
        <el-form-item label="通知医生" v-if="userRole === 'manager'">
          <el-switch v-model="markForm.notifyDoctor"></el-switch>
        </el-form-item>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="saveAbnormalMark">保存</el-button>
        <el-button @click="markDialogVisible = false">取消</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'QuestionnaireResult',
  data() {
    return {
      // 用户角色（医生/个案管理师）
      userRole: 'doctor',  // 可以根据实际登录用户角色动态设置
      
      // 搜索表单
      searchForm: {
        questionnaireType: '',
        patientName: '',
        submitTime: [],
        status: '',
        abnormal: ''
      },
      
      // 分页参数
      currentPage: 1,
      pageSize: 10,
      total: 0,
      
      // 数据加载状态
      loading: false,
      
      // 问卷结果列表
      resultList: [],
      
      // 详情对话框
      detailDialogVisible: false,
      currentDetail: null,
      
      // 标记异常对话框
      markDialogVisible: false,
      currentMarkRow: null,
      markForm: {
        type: '',
        level: 3,
        remark: '',
        notifyDoctor: true
      }
    };
  },
  
  created() {
    this.fetchData();
  },
  
  methods: {
    // 获取数据
    fetchData() {
      this.loading = true;
      
      // 这里模拟API请求，实际项目中应该调用真实的API
      setTimeout(() => {
        // 模拟数据
        this.resultList = [
          {
            id: '1',
            patientName: '许昊然',
            patientId: 'P100001',
            questionnaireName: '糖尿病随访问卷',
            questionnaireType: 'followup',
            submitTime: '2023-05-20 14:30:25',
            status: 'completed',
            abnormalMark: true,
            abnormalInfo: {
              type: 'healthIssue',
              level: 4,
              remark: '血糖数值异常，需及时关注'
            }
          },
          {
            id: '2',
            patientName: '苏雅婷',
            patientId: 'P100002',
            questionnaireName: '术后康复评估表',
            questionnaireType: 'assessment',
            submitTime: '2023-05-19 09:15:40',
            status: 'completed',
            abnormalMark: false
          },
          {
            id: '3',
            patientName: '林书辰',
            patientId: 'P100003',
            questionnaireName: '门诊满意度调查',
            questionnaireType: 'satisfaction',
            submitTime: '2023-05-18 16:45:33',
            status: 'completed',
            abnormalMark: false
          },
          {
            id: '4',
            patientName: '程若曦',
            patientId: 'P100004',
            questionnaireName: '高血压随访表',
            questionnaireType: 'followup',
            submitTime: '2023-05-17 11:20:15',
            status: 'completed',
            abnormalMark: true,
            abnormalInfo: {
              type: 'visitNeeded',
              level: 3,
              remark: '患者反映头痛症状加重，建议安排面诊'
            }
          },
          {
            id: '5',
            patientName: '郭子轩',
            patientId: 'P100005',
            questionnaireName: '肿瘤治疗副作用评估',
            questionnaireType: 'assessment',
            submitTime: '2023-05-22 08:50:10',
            status: 'incomplete',
            abnormalMark: false
          }
        ];
        
        this.total = this.resultList.length;
        this.loading = false;
      }, 500);
    },
    
    // 搜索
    handleSearch() {
      this.currentPage = 1;
      this.fetchData();
    },
    
    // 重置搜索
    resetSearch() {
      this.$refs.searchForm.resetFields();
      this.handleSearch();
    },
    
    // 刷新数据
    refreshData() {
      this.fetchData();
      this.$message.success('数据已刷新');
    },
    
    // 导出结果
    exportResults() {
      this.$message.success('问卷结果已导出');
      // 实际项目中这里应该调用导出API
    },
    
    // 分页大小变更
    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchData();
    },
    
    // 当前页变更
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchData();
    },
    
    // 获取问卷类型文本
    getQuestionnaireTypeText(type) {
      const typeMap = {
        'satisfaction': '满意度问卷',
        'followup': '随访问卷',
        'assessment': '评估问卷'
      };
      return typeMap[type] || type;
    },
    
    // 获取问卷类型标签样式
    getQuestionnaireTypeTag(type) {
      const tagMap = {
        'satisfaction': 'success',
        'followup': 'primary',
        'assessment': 'warning'
      };
      return tagMap[type] || '';
    },
    
    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        'completed': '已完成',
        'incomplete': '未完成',
        'expired': '已过期'
      };
      return statusMap[status] || status;
    },
    
    // 获取状态标签样式
    getStatusTag(status) {
      const tagMap = {
        'completed': 'success',
        'incomplete': 'info',
        'expired': 'danger'
      };
      return tagMap[status] || '';
    },
    
    // 获取行样式
    getRowClass({row}) {
      if (row.abnormalMark) {
        return 'abnormal-row';
      }
      return '';
    },
    
    // 点击行
    handleRowClick(row) {
      this.viewDetail(row);
    },
    
    // 查看详情
    viewDetail(row) {
      // 这里模拟API请求获取详细信息
      this.loading = true;
      
      setTimeout(() => {
        this.currentDetail = {
          ...row,
          questions: [
            {
              title: '您近一周的平均空腹血糖值是多少？',
              type: 'number',
              required: true,
              answer: 9.8,
              unit: 'mmol/L',
              isAbnormal: true,
              remark: '血糖值偏高，需密切关注'
            },
            {
              title: '您近一周是否按医嘱定时服药？',
              type: 'radio',
              required: true,
              answer: '是，完全按医嘱服药',
              isAbnormal: false,
              remark: ''
            },
            {
              title: '您近一周出现过哪些症状？',
              type: 'checkbox',
              required: true,
              answer: ['头晕', '口渴', '多尿'],
              isAbnormal: true,
              remark: '典型的高血糖症状，需要调整用药'
            },
            {
              title: '请描述您的饮食情况',
              type: 'text',
              required: false,
              answer: '每天三餐规律，但有时会吃点心和甜食。早餐通常是面包和牛奶，午餐会在单位食堂吃，晚餐在家吃，主要是米饭、蔬菜和肉类。',
              isAbnormal: false,
              remark: '建议减少碳水化合物和甜食摄入'
            },
            {
              title: '请对您目前的健康状况满意度评分',
              type: 'rate',
              required: true,
              answer: 3,
              isAbnormal: false,
              remark: ''
            }
          ],
          assessment: {
            score: 75,
            healthStatus: 'attention',
            followUpSuggestion: 'adjust',
            remarks: '患者血糖控制不佳，需调整用药方案，加强饮食管理指导。建议一周后复查血糖。'
          }
        };
        
        this.loading = false;
        this.detailDialogVisible = true;
      }, 300);
    },
    
    // 关闭详情对话框
    handleDetailDialogClose(done) {
      // 可以在这里添加关闭前的确认
      this.$confirm('评估内容可能未保存，确认关闭？')
        .then(_ => {
          done();
          this.currentDetail = null;
        })
        .catch(_ => {});
    },
    
    // 保存评估并关闭
    saveDetailAndClose() {
      // 这里模拟保存API调用
      this.$message.success('已成功保存问卷评估结果');
      this.detailDialogVisible = false;
      this.currentDetail = null;
      
      // 实际项目中应该调用API保存评估结果
    },
    
    // 标记问题异常
    markQuestionAbnormal(index) {
      if (!this.currentDetail) return;
      
      const question = this.currentDetail.questions[index];
      // 如果标记为异常但没有备注，弹出提示
      if (question.isAbnormal && !question.remark) {
        this.$message.warning('请为异常答案添加备注说明');
      }
      
      // 更新问卷的整体异常状态
      if (this.currentDetail.questions.some(q => q.isAbnormal)) {
        // 至少有一个问题被标记为异常
        this.currentDetail.abnormalMark = true;
      } else {
        // 没有问题被标记为异常
        this.currentDetail.abnormalMark = false;
      }
    },
    
    // 保存问题备注
    saveQuestionRemark(index) {
      // 实际项目中这里应该调用API保存备注
      console.log(`已保存问题${index + 1}的备注`);
    },
    
    // 标记问卷异常
    markAbnormal(row) {
      this.currentMarkRow = row;
      
      // 如果已经有异常标记，则加载已有信息
      if (row.abnormalMark && row.abnormalInfo) {
        this.markForm = { ...row.abnormalInfo };
      } else {
        // 否则重置表单
        this.markForm = {
          type: '',
          level: 3,
          remark: '',
          notifyDoctor: true
        };
      }
      
      this.markDialogVisible = true;
    },
    
    // 保存异常标记
    saveAbnormalMark() {
      if (!this.markForm.type) {
        this.$message.warning('请选择异常类型');
        return;
      }
      
      if (!this.markForm.remark) {
        this.$message.warning('请输入异常情况说明');
        return;
      }
      
      // 更新行的异常标记状态
      if (this.currentMarkRow) {
        // 如果当前是取消标记操作
        if (this.currentMarkRow.abnormalMark) {
          this.currentMarkRow.abnormalMark = false;
          this.currentMarkRow.abnormalInfo = null;
          this.$message.success('已取消异常标记');
        } else {
          // 添加异常标记
          this.currentMarkRow.abnormalMark = true;
          this.currentMarkRow.abnormalInfo = { ...this.markForm };
          
          // 如果需要通知医生
          if (this.markForm.notifyDoctor && this.userRole === 'manager') {
            this.$message({
              type: 'success',
              message: '已标记异常并通知主治医生'
            });
          } else {
            this.$message.success('已标记异常');
          }
        }
      }
      
      this.markDialogVisible = false;
      this.currentMarkRow = null;
    },
    
    // 联系患者
    contactPatient() {
      if (!this.currentDetail) return;
      
      this.$prompt('请输入要发送给患者的消息', '联系患者', {
        confirmButtonText: '发送',
        cancelButtonText: '取消',
        inputType: 'textarea'
      }).then(({ value }) => {
        this.$message({
          type: 'success',
          message: `已向患者 ${this.currentDetail.patientName} 发送消息`
        });
        
        // 实际项目中这里应该调用发送消息API
      }).catch(() => {
        // 取消操作
      });
    }
  }
};
</script>

<style scoped>
.questionnaire-result-container {
  padding: 20px;
}

.page-header-card, .search-card, .result-list-card {
  margin-bottom: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-left h2 {
  margin-right: 10px;
  margin-bottom: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.result-count {
  color: #909399;
  font-size: 14px;
}

.pagination-container {
  margin-top: 20px;
  text-align: center;
}

/* 问卷详情样式 */
.detail-header {
  margin-bottom: 20px;
}

.detail-header h3 {
  margin-top: 0;
  margin-bottom: 10px;
}

.detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  color: #606266;
}

.answer-list {
  margin-top: 20px;
}

.question-item {
  margin-bottom: 30px;
  padding: 15px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  background-color: #FAFAFA;
}

.question-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
}

.question-number {
  background-color: #409EFF;
  color: white;
  border-radius: 4px;
  padding: 2px 8px;
  margin-right: 10px;
  font-weight: bold;
}

.question-title {
  flex: 1;
  font-weight: bold;
}

.question-flags {
  margin-left: 10px;
}

.question-answer {
  background-color: #FFF;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 15px;
}

.answer-label {
  font-weight: bold;
  margin-right: 10px;
}

.answer-content {
  display: flex;
  align-items: center;
}

.text-answer {
  padding: 10px;
  background-color: #F5F7FA;
  border-radius: 4px;
  margin-top: 5px;
}

.number-answer {
  font-size: 18px;
  font-weight: bold;
  color: #303133;
}

.question-remarks {
  background-color: #F0F9EB;
  padding: 10px 15px;
  border-radius: 4px;
}

.remark-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.overall-assessment {
  padding: 20px;
  background-color: #F5F7FA;
  border-radius: 4px;
}

.score-hint {
  margin-left: 10px;
  color: #909399;
  font-size: 14px;
}

/* 异常行样式 */
.abnormal-row {
  background-color: #FFF0F0 !important;
}

/* 响应式调整 */
@media screen and (max-width: 768px) {
  .detail-meta {
    flex-direction: column;
    gap: 8px;
  }
  
  .question-header {
    flex-direction: column;
  }
  
  .question-flags {
    margin-left: 0;
    margin-top: 5px;
  }
  
  .answer-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .answer-label {
    margin-bottom: 5px;
  }
}
</style>